/**
 * STEP LIVE-PUBLISHER-GUARD-1 · §4·§5 — 관리자 긴급 종료 모달.
 *   · 기존 `/admin/broadcasts/{sid}/force-end` endpoint 를 호출 (신규 API 0).
 *   · 사유 입력 필수 · 2차 확인 · 종료 성공/부분 실패/실패 구분 표시.
 *   · 서버 측 관리자 권한 재검증은 그대로 유지.
 */
import React, { useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const AdminForceEndModal = ({ broadcast, onClose, onEnded }) => {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  if (!broadcast) return null;

  const canSubmit = reason.trim().length >= 3
                    && confirmText.trim() === broadcast.schedule_id
                    && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(
        `/admin/broadcasts/${broadcast.schedule_id}/force-end`,
        { reason: reason.trim(), seller_id: broadcast.seller_id,
          streaming_session_id: broadcast.streaming_session_id || undefined },
      );
      setResult(data);
      if (data?.summary === 'none') toast.info('남은 세션 없음');
      else if (data?.summary === 'partial') toast.warning('일부 종료 실패 · 다시 시도할 수 있어요');
      else toast.success('긴급 종료 완료');
      if (onEnded) try { onEnded(data); } catch { /* soft */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || '긴급 종료 실패');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4"
          data-testid="admin-force-end-modal">
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 shadow-2xl">
        <div className="flex items-start justify-between px-4 py-3 border-b border-black/5 dark:border-white/10">
          <div className="flex items-center gap-2 text-red-600 font-black">
            <AlertTriangle size={16} /> 방송 긴급 종료
          </div>
          <button type="button" onClick={onClose}
                   className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/10"
                   data-testid="admin-force-end-close">
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-3 space-y-3 text-sm">
          <div className="rounded-lg bg-red-500/5 border border-red-500/20 p-3 space-y-1">
            <div><span className="text-gray-500">판매자</span> · <span className="font-bold">{broadcast.seller_name || broadcast.seller_id}</span></div>
            <div><span className="text-gray-500">방송명</span> · <span className="font-bold truncate inline-block max-w-full align-bottom">{broadcast.title || '(제목 없음)'}</span></div>
            <div><span className="text-gray-500">상태</span> · <span className="font-bold">{broadcast.status || '-'}</span></div>
            <div><span className="text-gray-500">schedule_id</span> · <code className="text-[10px] bg-black/5 dark:bg-white/5 px-1 py-0.5 rounded">{broadcast.schedule_id}</code></div>
            <div><span className="text-gray-500">종료 대상 세션</span> · <span className="font-bold" data-testid="admin-force-end-target-count">{broadcast.open_session_count ?? 0}개</span>
              <span className="text-[10px] text-gray-500"> (DB 기록 기준)</span></div>
            <p className="text-[12px] font-black text-red-600">이 방송의 남은 세션을 모두 종료합니다.</p>
            {broadcast.streaming_session_id && (
              <div><span className="text-gray-500">streaming_session_id</span> · <code className="text-[10px] bg-black/5 dark:bg-white/5 px-1 py-0.5 rounded">{broadcast.streaming_session_id}</code></div>
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">종료 사유 (3자 이상)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                       placeholder="예: ghost publisher 감지로 소비자 재생 지속 — 운영 긴급 조치"
                       className="w-full px-2 py-1.5 rounded-md border border-black/10 dark:border-white/10 bg-white dark:bg-[#0F0F0F] text-sm"
                       data-testid="admin-force-end-reason" />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">
              확인을 위해 <code className="text-[10px] bg-black/5 dark:bg-white/5 px-1 py-0.5 rounded">{broadcast.schedule_id}</code> 를 그대로 입력하세요.
            </label>
            <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={broadcast.schedule_id}
                    className="w-full px-2 py-1.5 rounded-md border border-black/10 dark:border-white/10 bg-white dark:bg-[#0F0F0F] text-sm font-mono"
                    data-testid="admin-force-end-confirm-text" />
          </div>

          {result && (
            <div className="rounded-md bg-black/5 dark:bg-white/5 p-2 text-[11px] space-y-1" data-testid="admin-force-end-result">
              <div>schedule.status → <span className="font-bold">종료</span> · ended_at: {result.ended_at}</div>
              <div>phase57 ended_sessions: <span className="font-bold">{result.ended_sessions}</span></div>
              <div className="font-black" data-testid="admin-force-end-summary">
                {result.summary === 'none' ? '남은 세션 없음' : result.summary === 'partial' ? '일부 종료 실패' : '긴급 종료 완료'}
              </div>
              {(result.sessions || []).map((s) => (
                <div key={s.session_id} data-testid={`admin-force-end-session-${s.session_id}`}>
                  <code>{s.session_id}</code> · {s.prev_status} → <span className={`font-bold ${s.ok ? 'text-emerald-600' : 'text-red-600'}`}>{s.ok ? '종료·Room 삭제' : `실패 (${s.reason || '-'})`}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-black/5 dark:border-white/10">
          <button type="button" onClick={onClose} disabled={busy}
                   className="px-3 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-xs font-bold hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-40"
                   data-testid="admin-force-end-cancel">
            닫기
          </button>
          <button type="button" onClick={submit} disabled={!canSubmit}
                   className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-xs font-black shadow-sm"
                   data-testid="admin-force-end-submit">
            {busy && <Loader2 size={12} className="animate-spin" />}
            긴급 종료 실행
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminForceEndModal;
